import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CaseStudyReviewLinkDto } from 'tdc-web-backend/case-studies-review/schemas';
import { formatDateFns } from '../../../../../utils/helpers';
import StatusChipField from '../../../../../components/StatusChipField/StatusChipField';
import CustomButton from '../../../../../components/button/CustomButton';
import TableWithChipFilters from '../../../../../components/table/TableWithChipFilters';
import useTable from '../../../../../components/table/useTable';
import useCreate from '../../../../../utils/hooks/crud-hooks/useCreate';
import useGetOne from '../../../../../utils/hooks/crud-hooks/useGetOne';
import useNotification from '../../../../../utils/hooks/useNotification';
import DisplayLink from './components/DisplayLink';

const CaseStudyEmailLogs = ({
  reviewId,
  requestPublish,
}: {
  reviewId: string;
  requestPublish: boolean;
}) => {
  const { notify } = useNotification();

  const [open, setOpen] = useState(false);
  const [link, setLink] = useState<string | null>(null);

  const columns: GridColDef[] = [
    {
      field: 'created',
      headerName: 'Sent',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => (
        <Typography>{formatDateFns(params.value)}</Typography>
      ),
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      flex: 0.2,
      renderCell: (params: GridRenderCellParams) => params.row.recipients.join(', '),
    },
    {
      field: 'initiator',
      headerName: 'Initiator',
      flex: 0.3,
      renderCell: (params: GridRenderCellParams) =>
        `${params.row.initiator.firstName} ${params.row.initiator.lastName} (${params.row.initiator.email})`,
    },
    {
      field: 'success',
      headerName: 'Success',
      flex: 0.1,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value ? 'approved' : 'rejected';
        return <StatusChipField text={status} componentReturnType="dot" />;
      },
    },
    {
      field: 'errorMessage',
      headerName: 'Error message',
      flex: 0.1,
    },
  ];

  const { resourceLimit, chipFilters, totalRowsCount, loading, refetch, ...props } = useTable({
    resourceUrl: `emails/logs/cs-review/${reviewId}`,
    withTabs: false,
  });

  const { mutate: resend, isLoading: isLoadingResend } = useCreate({
    resource: `case-studies-review/${reviewId}/resend`,
  });

  const { refetch: fetchLink, isFetching: isFetchingLink } = useGetOne<CaseStudyReviewLinkDto>({
    resource: `case-studies-review/${reviewId}/link`,
    enabled: false,
    onSuccess: (data) => {
      setLink(data.data.link);
      setOpen(true);
    },
    onError: (error) => {
      notify.error('Something went wrong');
    },
  });

  const resendEmail = () => {
    resend(
      {},
      {
        onSuccess: () => {
          notify.success('Email was successfully resent.');
          refetch();
        },
        onError: () => {
          notify.error('Something went wrong');
        },
      },
    );
  };

  return (
    <>
      {link && open && <DisplayLink isOpen={open} setIsOpen={setOpen} link={link} />}

      <Stack spacing={3} sx={{ pt: 5 }}>
        <TableWithChipFilters
          numberOfRowsPerPage={resourceLimit}
          columns={columns}
          withElements={{
            withTitle: 'Email logs',
            withCta: !loading && totalRowsCount < 4 && !requestPublish && (
              <Stack direction="row" spacing={2}>
                <CustomButton
                  loading={isLoadingResend || isFetchingLink}
                  variant="secondary"
                  onClick={resendEmail}
                >
                  Resend email
                </CustomButton>
                <CustomButton
                  loading={isLoadingResend || isFetchingLink}
                  variant="secondary"
                  onClick={() => {
                    fetchLink();
                    setLink(null);
                  }}
                >
                  Generate link
                </CustomButton>
              </Stack>
            ),
          }}
          chipFilters={[]}
          sx={{
            '& .MuiDataGrid-cell': {
              overflowX: 'auto',
            },
            height: '320px',
          }}
          totalRowsCount={totalRowsCount}
          {...props}
        />
      </Stack>
    </>
  );
};
export default CaseStudyEmailLogs;
